import { type Query, type QueryClient, useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { client } from "@/hono";

/**
 * The open tickets whose scheduled span reaches the given window, earliest span first.
 *
 * A `week` ticket is measured by the whole week its stored date names, so one reaches the window
 * whenever any of its days does.
 */
export function useScheduledTickets(startDate: string, endDate: string) {
	/** Query */
	const query = useQuery({
		queryKey: ["tickets", "scheduled", startDate, endDate],
		queryFn: async () => {
			const response = await client.tickets.scheduled.$get({ query: { startDate, endDate } });
			const data = await responseHandler(response);
			return data.tickets;
		},
	});

	/** Return */
	return { ...query, tickets: guardArray(query.data) };
}

export type ScheduledTicketList = ReturnType<typeof useScheduledTickets>["tickets"];
export type ScheduledTicketListItem = ScheduledTicketList[number];

/** Whether a cached query is one of this hook's, whatever window it was asked for. */
export function isScheduledTicketsQuery({ queryKey }: Query) {
	return queryKey.includes("tickets") && queryKey.includes("scheduled");
}

/** The columns a schedule write answers with, and the only ones it can have changed. */
type ScheduledTicketPatch = Pick<
	ScheduledTicketListItem,
	| "id"
	| "startDate"
	| "endDate"
	| "dateMode"
	| "workStartDate"
	| "estimatedDays"
	| "_workExclusions"
>;

/**
 * Write a schedule write's answer into every window that holds the ticket, instead of fetching
 * the board again.
 *
 * A drag, a resize, a limiter or a break changes one ticket's five schedule columns and its
 * breaks, and the server answers with exactly those. Refetching the window for it meant every
 * ticket of every visible week came back down the wire — the largest read the board has — for
 * one row that was already known. The row is patched here and the query is left **stale**, so the
 * next focus or the next window still refreshes it, and nothing on screen waits for that.
 *
 * @param qc - The query client.
 * @param ticket - The server's answer to the write.
 */
export async function patchScheduledTicket(qc: QueryClient, ticket: ScheduledTicketPatch) {
	qc.setQueriesData<ScheduledTicketList>({ predicate: isScheduledTicketsQuery }, (tickets) => {
		if (!tickets) return tickets;
		let changed = false;
		const next = tickets.map((entry) => {
			if (entry.id !== ticket.id) return entry;
			changed = true;
			return {
				...entry,
				startDate: ticket.startDate,
				endDate: ticket.endDate,
				dateMode: ticket.dateMode,
				workStartDate: ticket.workStartDate,
				estimatedDays: ticket.estimatedDays,
				_workExclusions: ticket._workExclusions,
			};
		});
		return changed ? next : tickets;
	});
	// Stale, not refetched: the answer above is the truth for this ticket, and a refetch would only
	// carry every other ticket of the window back down for nothing. `refetchOnWindowFocus` and the
	// next window change pick the full read up again.
	await qc.invalidateQueries({ predicate: isScheduledTicketsQuery, refetchType: "none" });
}
